import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import CircularProgress from '@mui/material/CircularProgress';

import { Controller } from 'react-hook-form';

import { Box, Button, TextField, Typography, Paper, Autocomplete } from '@mui/material';
import apiService from '../../api/apiService';
import { FeedbackContext } from '../../App';
import { countryCodes, getHandleErrorWithFeedbackCX } from '../utility';

export default function ContactUs() {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const feedbackCX = useContext(FeedbackContext);
  const handleErrorWithFeedback = getHandleErrorWithFeedbackCX(feedbackCX);

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async data => {
    setIsLoading(true);
    try {
      await handleSendMessage(data);
      reset();
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      // already handled
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendMessage = data => {
    return apiService.createInstance('contact/', data)
      .then(() => {
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Your message has been sent!',
          autoHideDuration: 3000,
        });
      })
      .catch(error => {
        handleErrorWithFeedback({
          response: {
            status: error?.response?.status || 500,
            data: {
              text: error?.response?.data?.error || 'Something went wrong while sending message!',
            },
          },
        });
        throw error;
      });
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" width="100%" sx={{ backgroundColor: '#0d0d0d' }}>
      <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center" px={2}>
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            backgroundColor: '#f4ffcc',
            borderRadius: 2,
            width: '100%',
            maxWidth: 600,
            mx: 'auto',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#333', 
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h5" align="center" gutterBottom>
            Contact us
          </Typography>
          <Typography variant="h6" align="center" gutterBottom>
            Send us a Message
          </Typography>
          <Typography variant="body1" align="center" gutterBottom>
            We’d love to hear from you. Please fill out this form.
          </Typography>

          <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="First Name"
                fullWidth
                required
                {...register('firstName', { required: 'First name is required' })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                {...register('lastName', { required: 'Last name is required' })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Box>
            <Box sx={{ width: '100%' }} display="flex" gap={2} mb={2}>
              <Controller
                name="countryCode"
                control={control}
                rules={{ required: 'Country code is required' }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={countryCodes}
                    getOptionLabel={option => `${option.code}`}
                    onChange={(_, value) => field.onChange(value?.code || '')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Code"
                        // required
                        error={!!errors.countryCode}
                        helperText={errors.countryCode?.message}
                      />
                    )}
                  />
                )}
              />
              <TextField
                label="Phone Number"
                required
                fullWidth
                {...register('phone', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9\s()-]+$/,
                    message: 'Enter a valid phone number',
                  },
                })}
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Box>

            <TextField
              label="Email Address"
              fullWidth
              required
              margin="normal"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: 'Enter a valid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              label="Message"
              fullWidth
              required
              multiline
              rows={4}
              margin="normal"
              {...register('message', { required: 'Message is required' })}
              error={!!errors.message}
              helperText={errors.message?.message}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              sx={{
                mt: 3,
                backgroundColor: '#b9e85c',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#a5d74a',
                },
              }}
            >
              {/* Send Message */}
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: '#002b36',
          color: 'white',
          textAlign: 'center',
          py: 2,
        }}
      >
        <Typography variant="body2">
          Powered By <strong>Tallgrass</strong>, contact us on{' '}
          <a href="mailto:support@tallgrass.ai" style={{ color: 'white', textDecoration: 'underline' }}>
            support@tallgrass.ai
          </a>
        </Typography>
      </Box>
    </Box>
  );
}
